import { ref, computed, watch } from 'vue'
import { useQuery, keepPreviousData } from '@tanstack/vue-query'
import { useAlgoliaSearch } from '@/services/useAlgoliaSearch'
import { usePaginatableService } from '@/services/PaginatableService'
import type EntityService from '@/services/EntityService'
import { QueryFieldFilterConstraint } from 'firebase/firestore'

type UseSearchablePaginationOptions = {
  indexName: string
  service: EntityService<any>
  numPerPage?: number
  baseQuery?: QueryFieldFilterConstraint[]
  defaultOrderBy?: string
  defaultOrderDir?: 'asc' | 'desc'
}

export function useSearchablePagination<T>({
  indexName,
  service,
  numPerPage = 25,
  defaultOrderBy = 'createdAt',
  defaultOrderDir = 'asc',
  baseQuery = [],
}: UseSearchablePaginationOptions) {
  const queryText = ref('')
  const sortBy = ref<string | null>(defaultOrderBy)
  const sortDirection = ref<'asc' | 'desc'>(defaultOrderDir)

  const firestore = computed(() => usePaginatableService<T>(service, {
    numPerPage,
    orderByField: sortBy.value,
    orderByDirection: sortDirection.value,
    where: baseQuery,
  }))

  // Algolia search
  const algolia = useAlgoliaSearch<T>(indexName)
  algolia.perPage.value = numPerPage

  const isAlgolia = computed(() => queryText.value.trim().length > 0)

  // Firestore data fetching
  const {
    data: firestoreData,
    isPending: firestoreIsPending,
    isError: firestoreIsError,
    error: firestoreError,
    isPlaceholderData: firestoreIsPlaceholderData,
  } = useQuery({
    queryKey: ['firestore', service.name, sortBy, sortDirection, baseQuery, firestore.value.currentPage],
    queryFn: async () => {
      const res = await firestore.value.fetcher()
      return res.data
    },
    enabled: computed(() => !isAlgolia.value),
    placeholderData: keepPreviousData,
  })

  // Watch for search text changes
  watch(queryText, (text) => {
    if (text.trim().length > 0) {
      algolia.search(text)
    }
  })

  // Unified access: either Algolia (for search) or Firestore (for normal data fetching)
  const results = computed(() =>
    isAlgolia.value ? algolia.results.value : (firestoreData.value || [])
  )

  const isPending = computed(() =>
    isAlgolia.value ? algolia.isPending.value : firestoreIsPending.value
  )

  const isError = computed(() =>
    isAlgolia.value ? algolia.isError.value : firestoreIsError.value
  )

  const error = computed(() =>
    isAlgolia.value ? algolia.error.value : firestoreError.value
  )

  const isPlaceholderData = computed(() =>
    isAlgolia.value ? algolia.isPlaceholderData.value : firestoreIsPlaceholderData.value
  )

  const totalPages = computed(() =>
    isAlgolia.value ? algolia.totalPages.value : firestore.value.totalPageCount.value
  )

  const totalRecords = computed(() =>
    isAlgolia.value ? algolia.totalRecords.value : firestore.value.totalRecords.value
  )

  const currentPage = computed(() =>
    isAlgolia.value ? algolia.currentPage.value : firestore.value.currentPage.value
  )

  const nextPage = () => isAlgolia.value ? algolia.nextPage() : firestore.value.nextPage()
  const prevPage = () => isAlgolia.value ? algolia.prevPage() : firestore.value.prevPage()

  const search = (text: string) => {
    queryText.value = text
  }

  return {
    results,
    isPending,
    isError,
    error,
    isPlaceholderData,
    queryText,
    search,
    currentPage,
    totalPages,
    totalRecords,
    nextPage,
    prevPage,
    sortBy,
    sortDirection
  }
}
